/**
 * Epic 12, E12-US1 — „Exportovat do HTML" tlačítko v top-right panelu canvasu
 * (stejné umístění jako Auto-layout/Zobrazení/Sync/Change script). Sestavení
 * dokumentu (`export/htmlExport.ts#buildExportHtml`) i stažení proběhne čistě
 * na klientovi — žádné volání API, žádný záznam v historii, žádná mutace
 * projektu (epic-12 kap. 4/10 DoD). `effectiveTablePositions`/`tableSizes`
 * jsou stejné hodnoty, jaké `CanvasView.tsx` právě vykresluje, takže diagram
 * v exportu vypadá přesně jako aktuální stav editoru (E12-US2 AC).
 */
import { useCallback } from 'react';
import { FileDown } from 'lucide-react';
import type { CanvasMetadata, SchemaModel } from '@bits2schema/shared';
import { buildExportHtml } from '../export/htmlExport';
import { useI18n, useT } from '../i18n';
import type { BoxSize, Point } from './geometry';

interface ExportPanelProps {
  projectName: string;
  schema: SchemaModel;
  canvas: CanvasMetadata;
  effectiveTablePositions: Record<string, Point>;
  tableSizes: Record<string, BoxSize>;
}

function slugifyFileName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'schema-export';
}

export function ExportPanel({ projectName, schema, canvas, effectiveTablePositions, tableSizes }: ExportPanelProps) {
  const t = useT();
  const { language } = useI18n();

  const handleExport = useCallback(() => {
    const html = buildExportHtml({
      projectName,
      schema,
      canvas,
      tablePositions: effectiveTablePositions,
      tableSizes,
      t,
      language,
    });
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slugifyFileName(projectName)}.html`;
    link.click();
    URL.revokeObjectURL(url);
  }, [projectName, schema, canvas, effectiveTablePositions, tableSizes, t, language]);

  return (
    <button type="button" onClick={handleExport}>
      <FileDown size={16} />
      {t('export.button')}
    </button>
  );
}
